import { AxiosResponse } from 'axios';
import React, { useState } from 'react';

import { formatBytes } from '../../base/utils/helpers';
import useGlobalState from '../GlobalStateContext';
import { OptionalRolesOrPermissions } from '../helpers/Permissions';

import CRMApi from 'Api/CRM/CRMApi';
import { handleResponseError } from 'crm/helpers/ResponseErrorHelper';
import { FetchPaginatorParams } from 'crm/hooks/useAutoPaginator';

type Props = {
    action: string;
    title: string;
    defaultFilename?: string;
    params?: FetchPaginatorParams;
} & OptionalRolesOrPermissions;

export default function DownloadButton({
    action,
    title,
    roles = [],
    permissions = [],
    defaultFilename = 'noname.ext',
    params,
}: Props) {
    const [buttonText, setButtonText] = useState(title);
    const [isDownloading, setIsDownloading] = useState(false);
    const { loggedAdmin } = useGlobalState();

    function getDownloadText(loaded: number, total: number, computable: boolean) {
        if (loaded > 0) {
            if (computable) {
                return `Completed: ${Math.round((loaded * 100) / total)}%`;
            }
            return `Completed: ${formatBytes(loaded)}`;
        }
        return 'Downloading...';
    }

    function getFileNameFromHeaders(response: AxiosResponse) {
        const disposition = response.request.getResponseHeader('Content-Disposition');
        if (disposition && disposition.indexOf('attachment') > -1) {
            const matches = /filename[^;=\n]*=((['"]).*?|[^;\n]*)/.exec(disposition);
            if (matches?.[1]) {
                return matches[1].replace(/['"]/g, '');
            }
        }
        return defaultFilename;
    }

    async function handleDownload() {
        setIsDownloading(true);
        setButtonText(getDownloadText(0, 0, false));
        try {
            const response = await CRMApi.get(`download/${action}`, {
                responseType: 'blob',
                onDownloadProgress: (e) => {
                    setButtonText(getDownloadText(e.loaded, e.total, e.lengthComputable));
                },
                params,
            });

            const link = document.createElement('a');
            link.href = window.URL.createObjectURL(new Blob([response.data]));
            link.setAttribute('download', getFileNameFromHeaders(response));
            document.body.appendChild(link);
            link.click();
        } catch (error) {
            handleResponseError(error);
        } finally {
            setIsDownloading(false);
            setButtonText(title);
        }
    }

    return loggedAdmin.hasAccess(roles, permissions) ? (
        <button type="button" className="btn btn-primary btn-sm mr-3" disabled={isDownloading} onClick={handleDownload}>
            {buttonText}
        </button>
    ) : null;
}
